import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Users, Send, Trash2, Copy, Check, Download, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, Clock, Sparkles, ArrowDownToLine,
  Upload, FileText, X, Plus,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { api } from '../lib/api'
import type {
  CouncilSessionSummary,
  CouncilSessionDetail,
  CouncilPanelistReview,
  CouncilPanelistResponse,
  CouncilModeratorSynthesis,
  CouncilQualityTier,
} from '../lib/api'
import { KaijuLoader } from '../components/KaijuLoader'
import { notify } from '@/lib/notify'

// ============================================================================
// Persona metadata for display
// ============================================================================

const PERSONA_META: Record<string, { emoji: string; color: string; lightColor: string; label: string }> = {
  strategic_editor:       { emoji: '\u270F\uFE0F',       color: 'text-blue-400',    lightColor: 'text-blue-600',   label: 'Strategic Editor' },
  subject_matter_expert:  { emoji: '\uD83C\uDF93',       color: 'text-purple-400',  lightColor: 'text-purple-600', label: 'Subject Matter Expert' },
  devils_advocate:        { emoji: '\uD83D\uDE08',       color: 'text-red-400',     lightColor: 'text-red-600',    label: "Devil's Advocate" },
  gap_analyst:            { emoji: '\uD83D\uDD0D',       color: 'text-amber-400',   lightColor: 'text-amber-600',  label: 'Gap Analyst' },
  executive_reviewer:     { emoji: '\uD83D\uDCBC',       color: 'text-emerald-400', lightColor: 'text-emerald-600',label: 'Executive Reviewer' },
  fact_checker:           { emoji: '\u2705',             color: 'text-cyan-400',    lightColor: 'text-cyan-600',   label: 'Fact Checker' },
  humanizer:              { emoji: '\uD83D\uDDD2\uFE0F', color: 'text-pink-400',    lightColor: 'text-pink-600',   label: 'Humanizer' },
}

function scoreColor(score: number): string {
  if (score >= 8) return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30'
  if (score >= 5) return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30'
  return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30'
}

function statusIcon(status: string) {
  switch (status) {
    case 'completed': return <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />
    case 'failed': return <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
    case 'round1':
    case 'round2':
    case 'round3':
      return <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400 animate-pulse" />
    default: return <Clock className="w-4 h-4 text-gray-400" />
  }
}

// ============================================================================
// Quality Tier Selector
// ============================================================================

const TIER_INFO: Record<CouncilQualityTier, { label: string; desc: string; cost: string }> = {
  highest: { label: 'Highest Quality', desc: 'All Opus reviewers. Maximum depth.', cost: '$$$$' },
  high:    { label: 'High Quality',    desc: 'Sonnet reviewers + Opus moderator.', cost: '$$$' },
  balanced:{ label: 'Balanced',        desc: 'Sonnet core + Haiku support. Best value.', cost: '$$' },
  economy: { label: 'Economy',         desc: 'Haiku reviewers + Sonnet moderator.', cost: '$' },
}

function QualityTierSelector({ value, onChange }: { value: CouncilQualityTier; onChange: (t: CouncilQualityTier) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {(Object.keys(TIER_INFO) as CouncilQualityTier[]).map((tier) => {
        const info = TIER_INFO[tier]
        const selected = value === tier
        return (
          <button
            key={tier}
            onClick={() => onChange(tier)}
            className={`p-3 rounded-xl border text-left transition-all ${
              selected
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-500/30 shadow-sm'
                : 'border-gray-200/50 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20 hover:bg-white/80 dark:hover:bg-white/[0.07]'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-medium ${selected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                {info.label}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{info.cost}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{info.desc}</p>
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// Panelist Review Card
// ============================================================================

function PanelistReviewCard({ review }: { review: CouncilPanelistReview }) {
  const [expanded, setExpanded] = useState(false)
  const meta = PERSONA_META[review.persona] || { emoji: '?', color: 'text-gray-400', lightColor: 'text-gray-600', label: review.displayName }

  return (
    <div className="border border-gray-200/50 dark:border-white/10 rounded-xl bg-white/50 dark:bg-white/5 backdrop-blur-sm overflow-hidden transition-all hover:border-gray-300 dark:hover:border-white/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors"
      >
        <span className="text-lg">{meta.emoji}</span>
        <span className={`font-medium text-sm ${meta.lightColor} dark:${meta.color}`}>{meta.label}</span>
        <span className={`ml-auto px-2.5 py-0.5 text-xs font-mono rounded-lg border ${scoreColor(review.score)}`}>
          {review.score}/10
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-white/5">
          <div className="flex flex-wrap gap-1.5 mt-3 mb-3">
            {review.keyPoints.map((kp, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">{kp}</span>
            ))}
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 text-sm whitespace-pre-wrap">
            {review.review}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Panelist Debate Card (Round 2)
// ============================================================================

function PanelistDebateCard({ response }: { response: CouncilPanelistResponse }) {
  const [expanded, setExpanded] = useState(false)
  const meta = PERSONA_META[response.persona] || { emoji: '?', color: 'text-gray-400', lightColor: 'text-gray-600', label: response.displayName }

  return (
    <div className="border border-gray-200/50 dark:border-white/10 rounded-xl bg-white/50 dark:bg-white/5 backdrop-blur-sm overflow-hidden transition-all hover:border-gray-300 dark:hover:border-white/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors"
      >
        <span className="text-lg">{meta.emoji}</span>
        <span className={`font-medium text-sm ${meta.lightColor} dark:${meta.color}`}>{meta.label}</span>
        <span className={`ml-auto px-2.5 py-0.5 text-xs font-mono rounded-lg border ${scoreColor(response.revisedScore)}`}>
          {response.revisedScore}/10
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-white/5 space-y-3">
          {response.agreements.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1.5">Agrees with:</p>
              <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                {response.agreements.map((a, i) => <li key={i} className="flex gap-1.5"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 shrink-0" />{a}</li>)}
              </ul>
            </div>
          )}
          {response.disagreements.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1.5">Disagrees with:</p>
              <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                {response.disagreements.map((d, i) => <li key={i} className="flex gap-1.5"><XCircle className="w-3.5 h-3.5 mt-0.5 text-red-500 shrink-0" />{d}</li>)}
              </ul>
            </div>
          )}
          <div className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 text-sm whitespace-pre-wrap">
            {response.response}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Synthesis Panel (Round 3)
// ============================================================================

const DOWNLOAD_FORMATS = [
  { key: 'md' as const, label: 'Markdown (.md)', icon: FileText },
  { key: 'txt' as const, label: 'Plain Text (.txt)', icon: FileText },
  { key: 'docx' as const, label: 'Word (.docx)', icon: FileText },
  { key: 'pdf' as const, label: 'PDF (.pdf)', icon: FileText },
]

function SynthesisPanel({
  synthesis,
  revisedDocument,
  session,
  onAdopt,
  adopting,
}: {
  synthesis: CouncilModeratorSynthesis
  revisedDocument: string | null
  session: CouncilSessionDetail
  onAdopt: () => void
  adopting: boolean
}) {
  const [copied, setCopied] = useState(false)
  const [showDebate, setShowDebate] = useState(false)
  const [showDownloadMenu, setShowDownloadMenu] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const downloadMenuRef = useRef<HTMLDivElement>(null)
  const canAdopt = session.sourceArtifactType && session.sourceArtifactType !== 'adhoc' && session.sourceArtifactId && !session.adoptedAt

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false)
      }
    }
    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDownloadMenu])

  async function handleCopy() {
    if (!revisedDocument) return
    await navigator.clipboard.writeText(revisedDocument)
    setCopied(true)
    notify.success('Copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleDownload(format: 'md' | 'txt' | 'docx' | 'pdf') {
    setShowDownloadMenu(false)
    setDownloading(true)
    try {
      await api.council.download(session.id, format)
      notify.success(`Downloaded as ${format.toUpperCase()}`)
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Consensus score */}
      <div className="flex items-center gap-4">
        <div className={`px-5 py-3 rounded-xl border text-2xl font-bold font-mono ${scoreColor(synthesis.consensusScore)}`}>
          {synthesis.consensusScore}/10
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Council Consensus Score</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Weighted average after 3-round deliberation</p>
        </div>
      </div>

      {/* Unanimous findings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {synthesis.unanimousStrengths.length > 0 && (
          <div className="border border-green-200 dark:border-green-500/20 rounded-xl p-4 bg-green-50 dark:bg-green-500/5">
            <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2">Unanimous Strengths</p>
            <ul className="text-xs text-green-800 dark:text-gray-300 space-y-1.5">
              {synthesis.unanimousStrengths.map((s, i) => <li key={i} className="flex gap-1.5"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 shrink-0" />{s}</li>)}
            </ul>
          </div>
        )}
        {synthesis.unanimousWeaknesses.length > 0 && (
          <div className="border border-red-200 dark:border-red-500/20 rounded-xl p-4 bg-red-50 dark:bg-red-500/5">
            <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">Unanimous Weaknesses</p>
            <ul className="text-xs text-red-800 dark:text-gray-300 space-y-1.5">
              {synthesis.unanimousWeaknesses.map((w, i) => <li key={i} className="flex gap-1.5"><XCircle className="w-3.5 h-3.5 mt-0.5 text-red-500 shrink-0" />{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Recommendations (Priority Order)</p>
        <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          {synthesis.recommendations.map((rec, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="text-primary-600 dark:text-primary-400 font-mono text-xs font-bold mt-0.5 shrink-0">{i + 1}.</span>
              <span>{rec}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Key debate points (collapsible) */}
      {synthesis.keyDebatePoints.length > 0 && (
        <div>
          <button
            onClick={() => setShowDebate(!showDebate)}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {showDebate ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Key Debate Points ({synthesis.keyDebatePoints.length})
          </button>
          {showDebate && (
            <div className="mt-3 space-y-3">
              {synthesis.keyDebatePoints.map((dp, i) => (
                <div key={i} className="border border-gray-200/50 dark:border-white/10 rounded-xl p-4 bg-white/50 dark:bg-white/5">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{dp.topic}</p>
                  <div className="space-y-1.5 mb-2">
                    {dp.positions.map((pos, j) => (
                      <p key={j} className="text-xs text-gray-500 dark:text-gray-400">
                        <span className={`font-medium ${PERSONA_META[pos.persona]?.lightColor || 'text-gray-600'} dark:${PERSONA_META[pos.persona]?.color || 'text-gray-400'}`}>
                          {PERSONA_META[pos.persona]?.label || pos.persona}:
                        </span>{' '}
                        {pos.position}
                      </p>
                    ))}
                  </div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 italic">Resolution: {dp.resolution}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Revised document */}
      {revisedDocument && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Revised Document</p>
            <div className="flex items-center gap-1.5">
              {canAdopt && (
                <button
                  onClick={onAdopt}
                  disabled={adopting}
                  className="btn-secondary flex items-center gap-1.5 text-xs !px-3 !py-1.5"
                >
                  {adopting ? <KaijuLoader size="sm" /> : <ArrowDownToLine className="w-3.5 h-3.5" />}
                  {adopting ? 'Adopting...' : 'Adopt Revision'}
                </button>
              )}
              {session.adoptedAt && (
                <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-2">
                  <CheckCircle className="w-3.5 h-3.5" /> Adopted
                </span>
              )}
              <button onClick={handleCopy} className="btn-ghost !p-2 !rounded-lg" title="Copy">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
              <div className="relative" ref={downloadMenuRef}>
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={downloading}
                  className="btn-ghost !p-2 !rounded-lg flex items-center gap-1"
                  title="Download"
                >
                  {downloading ? <KaijuLoader size="sm" /> : <Download className="w-4 h-4" />}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white dark:bg-surface-800 border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                    {DOWNLOAD_FORMATS.map((fmt) => (
                      <button
                        key={fmt.key}
                        onClick={() => handleDownload(fmt.key)}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white flex items-center gap-2.5 transition-colors"
                      >
                        <fmt.icon className="w-4 h-4 text-gray-400" />
                        <span>{fmt.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border border-gray-200/50 dark:border-white/10 rounded-xl p-5 bg-white/50 dark:bg-white/5 backdrop-blur-sm prose prose-sm dark:prose-invert max-w-none max-h-[600px] overflow-y-auto">
            <ReactMarkdown>{revisedDocument}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Progress Stepper
// ============================================================================

function ProgressStepper({ status }: { status: string }) {
  const steps = [
    { key: 'round1', label: 'Round 1: Reviews', sublabel: '7 panelists' },
    { key: 'round2', label: 'Round 2: Debate', sublabel: '7 responses' },
    { key: 'round3', label: 'Round 3: Synthesis', sublabel: 'Moderator' },
  ]
  const statusOrder = ['pending', 'round1', 'round2', 'round3', 'completed']
  const currentIdx = statusOrder.indexOf(status)

  return (
    <div className="flex items-center gap-2">
      {steps.map((step, i) => {
        const stepIdx = i + 1
        const isCompleted = currentIdx > stepIdx
        const isActive = currentIdx === stepIdx

        return (
          <div key={step.key} className="flex items-center gap-2 flex-1">
            <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border flex-1 transition-all ${
              isCompleted ? 'border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10' :
              isActive ? 'border-primary-300 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 animate-pulse' :
              'border-gray-200/50 dark:border-white/10 bg-white/50 dark:bg-white/5'
            }`}>
              {isCompleted && <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 shrink-0" />}
              {isActive && <KaijuLoader size="sm" />}
              {!isCompleted && !isActive && <Clock className="w-4 h-4 text-gray-400 shrink-0" />}
              <div>
                <p className={`text-xs font-medium ${
                  isActive ? 'text-primary-600 dark:text-primary-400' :
                  isCompleted ? 'text-green-600 dark:text-green-400' :
                  'text-gray-400 dark:text-gray-500'
                }`}>
                  {step.label}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500">{step.sublabel}</p>
              </div>
            </div>
            {i < steps.length - 1 && <div className={`w-6 h-px shrink-0 ${isCompleted ? 'bg-green-300 dark:bg-green-500/50' : 'bg-gray-200 dark:bg-white/10'}`} />}
          </div>
        )
      })}
    </div>
  )
}

// ============================================================================
// Main Council Page
// ============================================================================

export function CouncilPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState<CouncilSessionSummary[]>([])
  const [loadingSessions, setLoadingSessions] = useState(true)
  const [activeSession, setActiveSession] = useState<CouncilSessionDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  // Submit form state
  const [documentText, setDocumentText] = useState('')
  const [documentTitle, setDocumentTitle] = useState('')
  const [qualityTier, setQualityTier] = useState<CouncilQualityTier>('balanced')
  const [submitting, setSubmitting] = useState(false)

  // Round collapse state
  const [showRound1, setShowRound1] = useState(true)
  const [showRound2, setShowRound2] = useState(true)

  const [adopting, setAdopting] = useState(false)

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: number } | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const ACCEPTED_TYPES = '.pdf,.docx,.doc,.pptx,.xlsx,.xls,.csv,.tsv,.txt,.md,.markdown'

  const handleFileUpload = useCallback(async (file: File) => {
    setExtracting(true)
    setUploadedFile({ name: file.name, size: file.size })
    try {
      const result = await api.council.extractText(file)
      setDocumentText(result.text)
      if (!documentTitle) {
        setDocumentTitle(file.name.replace(/\.[^.]+$/, ''))
      }
      notify.success(`Extracted text from ${file.name}`)
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to extract text from file')
      setUploadedFile(null)
    } finally {
      setExtracting(false)
    }
  }, [documentTitle])

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragging(true)
  }

  useEffect(() => {
    loadSessions()
  }, [])

  useEffect(() => {
    const sessionId = searchParams.get('session')
    if (sessionId) {
      loadSessionDetail(sessionId)
    }
  }, [searchParams])

  async function loadSessions() {
    setLoadingSessions(true)
    try {
      const res = await api.council.sessions.list()
      setSessions(res.sessions)
    } catch (err) {
      console.error('Failed to load council sessions:', err)
    } finally {
      setLoadingSessions(false)
    }
  }

  async function loadSessionDetail(id: string) {
    setLoadingDetail(true)
    try {
      const session = await api.council.sessions.get(id)
      setActiveSession(session)
    } catch (err) {
      console.error('Failed to load session:', err)
      notify.error('Failed to load session')
    } finally {
      setLoadingDetail(false)
    }
  }

  async function handleSubmit() {
    if (!documentText.trim()) return
    setSubmitting(true)

    const promise = api.council.submit({
      documentText,
      documentTitle: documentTitle || undefined,
      qualityTier,
    })

    notify.aiGeneration(promise, {
      loading: 'Council is deliberating... (15 AI calls across 3 rounds)',
      success: 'Council review complete!',
      error: 'Council review failed',
    })

    try {
      const session = await promise
      setActiveSession(session)
      setSearchParams({ session: session.id })
      setDocumentText('')
      setDocumentTitle('')
      loadSessions()
    } catch {
      // handled by notify
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.council.sessions.delete(id)
      notify.success('Session deleted')
      if (activeSession?.id === id) {
        setActiveSession(null)
        setSearchParams({})
      }
      loadSessions()
    } catch {
      notify.error('Failed to delete session')
    }
  }

  async function handleAdopt() {
    if (!activeSession) return
    setAdopting(true)
    try {
      await api.council.adopt(activeSession.id)
      notify.success('Revision adopted successfully')
      loadSessionDetail(activeSession.id)
    } catch {
      notify.error('Failed to adopt revision')
    } finally {
      setAdopting(false)
    }
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Session History Sidebar */}
      <div className="w-72 border-r border-gray-200/50 dark:border-white/10 flex flex-col bg-white/40 dark:bg-surface-900/60 backdrop-blur-lg shrink-0">
        <div className="p-4 border-b border-gray-200/50 dark:border-white/10">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            LLM Council
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Multi-persona document review</p>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
          {loadingSessions ? (
            <div className="flex justify-center py-8"><KaijuLoader size="sm" /></div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-10 px-4">
              <Users className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No sessions yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Submit a document to get started</p>
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSearchParams({ session: s.id })
                  loadSessionDetail(s.id)
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearchParams({ session: s.id }); loadSessionDetail(s.id) } }}
                className={`group w-full text-left p-3.5 hover:bg-gray-50/80 dark:hover:bg-white/5 transition-colors cursor-pointer ${
                  activeSession?.id === s.id ? 'bg-primary-50/60 dark:bg-white/10 border-l-2 border-l-primary-500' : ''
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {statusIcon(s.status)}
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                    {s.documentTitle || 'Untitled'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(s.id) }}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 dark:hover:text-red-400 text-gray-400 dark:text-gray-500 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{new Date(s.createdAt).toLocaleDateString()}</span>
                  {s.consensusScore != null && (
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono border ${scoreColor(s.consensusScore)}`}>
                      {s.consensusScore}/10
                    </span>
                  )}
                  {s.adoptedAt && <CheckCircle className="w-3 h-3 text-green-500 dark:text-green-400" />}
                </div>
              </div>
            ))
          )}
        </div>

        {/* New submission button */}
        <div className="p-3 border-t border-gray-200/50 dark:border-white/10">
          <button
            onClick={() => { setActiveSession(null); setSearchParams({}) }}
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Review
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 dark:bg-transparent">
        {loadingDetail ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <KaijuLoader size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading session...</p>
          </div>
        ) : activeSession ? (
          /* Results View */
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    {activeSession.documentTitle || 'Council Review'}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/10">
                      {TIER_INFO[activeSession.qualityTier as CouncilQualityTier]?.label || activeSession.qualityTier}
                    </span>
                    <span>{new Date(activeSession.createdAt).toLocaleString()}</span>
                    {activeSession.customerName && (
                      <span className="px-2 py-0.5 rounded-md bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400">
                        {activeSession.customerName}
                      </span>
                    )}
                    {activeSession.sourceArtifactType && activeSession.sourceArtifactType !== 'adhoc' && (
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 capitalize">
                        {activeSession.sourceArtifactType.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                </div>
                {activeSession.round3Synthesis && (
                  <div className={`px-4 py-2 rounded-xl border text-xl font-bold font-mono ${scoreColor(activeSession.round3Synthesis.consensusScore)}`}>
                    {activeSession.round3Synthesis.consensusScore}/10
                  </div>
                )}
              </div>
            </div>

            {/* Progress Stepper */}
            {activeSession.status !== 'completed' && activeSession.status !== 'failed' && (
              <ProgressStepper status={activeSession.status} />
            )}

            {activeSession.status === 'failed' && (
              <div className="card !bg-red-50 dark:!bg-red-500/10 !border-red-200 dark:!border-red-500/30">
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Council review failed. Try submitting again.
                </p>
              </div>
            )}

            {/* Round 1 */}
            {activeSession.round1Reviews && (
              <div className="card">
                <button
                  onClick={() => setShowRound1(!showRound1)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-1 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {showRound1 ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Round 1: Independent Reviews ({activeSession.round1Reviews.length} panelists)
                </button>
                {showRound1 && (
                  <div className="space-y-2 mt-3">
                    {activeSession.round1Reviews.map((r) => (
                      <PanelistReviewCard key={r.persona} review={r} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Round 2 */}
            {activeSession.round2Reviews && (
              <div className="card">
                <button
                  onClick={() => setShowRound2(!showRound2)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-1 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {showRound2 ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Round 2: Debate ({activeSession.round2Reviews.length} responses)
                </button>
                {showRound2 && (
                  <div className="space-y-2 mt-3">
                    {activeSession.round2Reviews.map((r) => (
                      <PanelistDebateCard key={r.persona} response={r} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Round 3: Synthesis */}
            {activeSession.round3Synthesis && (
              <div className="card">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Round 3: Council Synthesis
                </h3>
                <SynthesisPanel
                  synthesis={activeSession.round3Synthesis}
                  revisedDocument={activeSession.revisedDocument}
                  session={activeSession}
                  onAdopt={handleAdopt}
                  adopting={adopting}
                />
              </div>
            )}
          </div>
        ) : (
          /* Submit Form */
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 mb-4">
                <Users className="w-7 h-7 text-primary-600 dark:text-primary-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">LLM Council</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 max-w-md mx-auto">
                Submit any document for multi-persona review, critique, and revision by a panel of 7 AI reviewers
              </p>
            </div>

            <div className="card space-y-5">
              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Document Title (optional)</label>
                <input
                  type="text"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="e.g., Q3 Territory Strategy Brief"
                  className="input"
                />
              </div>

              {/* File upload zone */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Upload Document or Paste Text</label>

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={() => setDragging(false)}
                  onClick={() => !extracting && fileInputRef.current?.click()}
                  className={`relative rounded-xl border-2 border-dashed p-5 mb-3 text-center cursor-pointer transition-all ${
                    dragging
                      ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                      : uploadedFile
                        ? 'border-green-300 dark:border-green-500/30 bg-green-50 dark:bg-green-500/5'
                        : 'border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.07]'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload(file)
                      e.target.value = ''
                    }}
                  />

                  {extracting ? (
                    <div className="flex items-center justify-center gap-2 py-2">
                      <KaijuLoader size="sm" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">Extracting text from {uploadedFile?.name}...</span>
                    </div>
                  ) : uploadedFile ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">{uploadedFile.name}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">({(uploadedFile.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setUploadedFile(null)
                          setDocumentText('')
                          setDocumentTitle('')
                        }}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="py-2">
                      <Upload className="w-7 h-7 text-gray-300 dark:text-gray-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Drop a file here or <span className="text-primary-600 dark:text-primary-400 font-medium">browse</span>
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        PDF, Word, PowerPoint, Excel, CSV, Markdown, Text
                      </p>
                    </div>
                  )}
                </div>

                {/* Text area */}
                <textarea
                  value={documentText}
                  onChange={(e) => setDocumentText(e.target.value)}
                  placeholder="...or paste the document text directly here"
                  rows={10}
                  className="input !font-mono resize-y"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                  {documentText.length > 0 ? `${documentText.length.toLocaleString()} characters` : 'Supports markdown formatting'}
                </p>
              </div>

              {/* Quality tier */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Quality Tier</label>
                <QualityTierSelector value={qualityTier} onChange={setQualityTier} />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || !documentText.trim()}
                className="w-full btn-primary flex items-center justify-center gap-2 !py-3 text-base"
              >
                {submitting ? (
                  <>
                    <KaijuLoader size="sm" />
                    Council is deliberating...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit to Council
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                15 AI calls across 3 debate rounds. Takes 30-90 seconds depending on quality tier.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
